import { motion } from "framer-motion";
import { Search, Compass, Palette, MessageSquare, Rocket } from "lucide-react";
import ApplyCta from "./ApplyCta";

const stages = [
  {
    icon: Search,
    number: "01",
    title: "Diagnóstico Estratégico",
    description: "Detectamos las grietas de percepción y definimos el punto de partida real.",
    accent: "from-[#344d75] to-[#5b7cab]",
  },
  {
    icon: Compass,
    number: "02",
    title: "Plataforma y Estrategia",
    description: "Ordenamos tus unidades de negocio y servicios para que crezcan sin canibalizarse.",
    accent: "from-[#5b7cab] to-[#7ab7fd]",
  },
  {
    icon: Palette,
    number: "03",
    title: "Identidad Visual",
    description: "Logotipo, tipografías, cromatismo y reglas de uso. Estética de alto nivel diseñada para imponer respeto.",
    accent: "from-[#344d75] to-[#5b7cab]",
  },
  {
    icon: MessageSquare,
    number: "04",
    title: "Sistema Narrativo",
    description: "Definimos qué decís y cómo lo decís. Comunicación automática y coherente.",
    accent: "from-[#5b7cab] to-[#7ab7fd]",
  },
  {
    icon: Rocket,
    number: "05",
    title: "Proyección Estratégica",
    description: "El plan para que tu marca soporte los próximos 10 años de visión.",
    accent: "from-[#344d75] to-[#5b7cab]",
  },
];

const Deliverables = () => {
  return (
    <section className="relative py-28 md:py-36 overflow-hidden" style={{ background: "linear-gradient(180deg, hsl(217 61% 10%), hsl(220 69% 4%))" }}>
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "linear-gradient(hsl(216 32% 52%) 1px, transparent 1px), linear-gradient(90deg, hsl(216 32% 52%) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />

      {/* Glow effect */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-10" style={{
        background: "radial-gradient(circle, hsl(216 38% 33%), transparent 70%)",
      }} />

      <div className="container px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1]">
            La infraestructura de tu nueva{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#5b7cab] to-[#7ab7fd]">
              autoridad.
            </span>
          </h2>
        </motion.div>

        {/* Timeline stages */}
        <div className="max-w-5xl mx-auto">
          {stages.map((stage, index) => (
            <motion.div
              key={stage.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative"
            >
              {/* Connector line */}
              {index < stages.length - 1 && (
                <div className="absolute left-[39px] md:left-[47px] top-[88px] w-px h-[calc(100%-56px)] bg-gradient-to-b from-[#5b7cab]/30 to-transparent" />
              )}

              <div className="flex gap-5 md:gap-8 items-start py-6 group">
                {/* Number circle */}
                <div className="flex-shrink-0 relative">
                  <div className={`w-[78px] h-[78px] md:w-[94px] md:h-[94px] rounded-2xl bg-gradient-to-br ${stage.accent} p-[1px] transition-all duration-500 group-hover:shadow-[0_0_30px_hsl(216_32%_52%/0.3)]`}>
                    <div className="w-full h-full rounded-2xl bg-[hsl(217,61%,10%)] flex flex-col items-center justify-center gap-0.5">
                      <span className="text-[#5b7cab] text-[11px] font-mono tracking-wider opacity-60">ETAPA</span>
                      <span className="text-white text-2xl md:text-3xl font-display font-bold">{stage.number}</span>
                    </div>
                  </div>
                </div>

                {/* Content card */}
                <div className="flex-1 pt-1">
                  <div className="p-6 md:p-8 rounded-2xl border border-[#5b7cab]/15 bg-white/[0.02] backdrop-blur-sm transition-all duration-500 group-hover:border-[#5b7cab]/30 group-hover:bg-white/[0.04]">
                    <div className="flex items-center gap-3 mb-3">
                      <stage.icon className="w-5 h-5 text-[#7ab7fd]" strokeWidth={1.5} />
                      <h3 className="font-display text-xl md:text-2xl font-semibold text-white">
                        {stage.title}
                      </h3>
                    </div>
                    <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-xl">
                      {stage.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <ApplyCta variant="dark" />
      </div>
    </section>
  );
};

export default Deliverables;
